import { Help } from '@mui/icons-material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { get, set } from 'lodash';
import React, { FC, useMemo } from 'react';
import { FORM_FIELDS_TYPES } from '../../../../../lib/constants/constant';
import { MUIFieldProps } from '../../../../../lib/types/types';
import { DisplayDate, DisplayDecimal, DisplayEuro } from '../../../../../utils/formats';
import { mapTablePath } from '../../../../../utils/formStruc';
import { enumT, fieldNameTranslation, isTrue } from '../../../../../utils/helper';
import MUIToolTip from '../../../mui-tooltip/MUIToolTip';
import FormFields from '../FormFields';
import SingleUpload from './SingleUpload';

const SimpleZeitplanungTable: FC<MUIFieldProps> = (props) => {
	const { fieldValue, updateFieldValue, isDisabled } = props;

	const component = get(props, 'component');
	const componentPart = component.split('.');
	const apiName = useMemo(() => {
		if (get(props, 'api')) {
			return get(props, 'api');
		}
		return componentPart[0] + 's';
	}, [componentPart, props]);

	const columns = get(props, 'schema.attributes');
	const tablePath = get(props, 'path', '');
	const width = get(props, 'width', '100%');

	const rowTypeField = get(props, 'simpleZeitplanungProps.rowTypeField', 'Zelle_6');
	const aufgabeValue = get(props, 'simpleZeitplanungProps.aufgabeValue', 'Aufgabe');
	const aufgabeLabel = get(props, 'simpleZeitplanungProps.aufgabeLabel', 'Arbeitspaket');
	const milestoneValue = get(props, 'simpleZeitplanungProps.milestoneValue', 'Meilenstein');
	const hideColumnsForMilestone = get(props, 'simpleZeitplanungProps.hideColumnsForMilestone', ['Zelle_4']);
	const addAufgabeLabel = get(props, 'simpleZeitplanungProps.addAufgabeLabel', `${aufgabeLabel} hinzufügen`);
	const addMilestoneLabel = get(props, 'simpleZeitplanungProps.addMilestoneLabel', `${milestoneValue} hinzufügen`);

	const isFieldDisabled = (columnKey: string) => {
		if (isDisabled) return true;
		if (get(props, 'disabledColumns', []).includes(columnKey)) {
			return true;
		}
		return false;
	};

	const createRow = (typeValue: string) => {
		const row = Object.keys(columns).reduce((acc: Record<string, any>, key: string) => {
			acc[key] = null;
			return acc;
		}, {});
		row[rowTypeField] = typeValue;
		return row;
	};

	const pushRow = (row: Record<string, any>) => {
		const newValue = [...(fieldValue || []), row];
		let e = { target: { value: newValue } };
		updateFieldValue(e as unknown as React.ChangeEvent<HTMLInputElement>);
	};

	const handleAddAufgabe = () => {
		pushRow(createRow(aufgabeValue));
	};

	const handleAddMilestone = () => {
		if ((fieldValue || []).length === 0) {
			const firstArbeitspaket = createRow(aufgabeValue);
			const firstMilestone = createRow(milestoneValue);
			hideColumnsForMilestone.forEach((colKey: string) => {
				firstMilestone[colKey] = null;
			});

			let e = { target: { value: [firstArbeitspaket, firstMilestone] } };
			updateFieldValue(e as unknown as React.ChangeEvent<HTMLInputElement>);
			return;
		}

		const milestoneRow = createRow(milestoneValue);
		hideColumnsForMilestone.forEach((colKey: string) => {
			milestoneRow[colKey] = null;
		});
		pushRow(milestoneRow);
	};

	const handleChange = (path: any, value: any, rowIndex: number) => {
		const radioGroups = get(props, 'tableProps.radioGroups', []);
		if (radioGroups.includes(path) && value === true) {
			radioGroups.forEach((group: string) => {
				if (group !== path) {
					set(fieldValue, `${rowIndex}.${group}`, false);
				}
			});
		}

		set(fieldValue, `${rowIndex}.${path}`, value);

		let e = { target: { value: fieldValue } };
		updateFieldValue(e as React.ChangeEvent<HTMLInputElement>);
	};

	const handleDelete = (rowIndex: number) => {
		const newRow = (fieldValue || []).filter((_: any, index: number) => index !== rowIndex);
		let e = { target: { value: newRow } };
		updateFieldValue(e as React.ChangeEvent<HTMLInputElement>);
	};

	const visibleColumns = Object.keys(columns).filter((key: string) => !get(props, 'hideColumns', []).includes(key));
	const showColGroups = isTrue(get(props, 'colGroups'));
	const startingKey = get(props, 'colGroupsProps.startingKey');
	const colSpan = get(props, 'colGroupsProps.span', 1);
	const label = get(props, 'colGroupsProps.label', '');
	const spannedColTooltip = get(props, 'colGroupsProps.tooltip', '');

	const getRowBackground = (row: any) => (get(row, rowTypeField) === milestoneValue ? '#eef5f9' : 'inherit');

	const getDisplayValue = (columnKey: string, row: any) => {
		if (columnKey === rowTypeField && get(row, rowTypeField) === aufgabeValue) {
			return aufgabeLabel;
		}
		return row[columnKey];
	};

	let skip = 0;
	return (
		<>
			<Box sx={{ mt: '1rem' }}>
				{get(props, 'tableLabel', '') && (
					<Typography sx={{ mb: 1, fontWeight: 600 }}>
						{get(props, 'tableLabel', '')}{' '}
						{get(props, 'tableLabelTooltip', '') ?
							<MUIToolTip title={get(props, 'tableLabelTooltip', '')}>
								<Help fontSize='small' color='primary' sx={{ ml: '5px' }} />{' '}
							</MUIToolTip>
						:	<></>}
					</Typography>
				)}
				<TableContainer component={'div'} sx={{ width: width }}>
					<Table className='editable-table'>
						<TableHead>
							{showColGroups && (
								<TableRow>
									{visibleColumns.map((eachCol: string, i: number) => {
										if (skip > 0) {
											skip--;
											return null;
										}
										if (eachCol === startingKey) {
											skip = colSpan - 1;
											return (
												<TableCell key={startingKey + i} colSpan={colSpan}>
													{label}
													{spannedColTooltip && (
														<MUIToolTip title={spannedColTooltip}>
															<Help fontSize='small' color='primary' sx={{ ml: '5px' }} />
														</MUIToolTip>
													)}
												</TableCell>
											);
										}
										return <TableCell key={startingKey + i} rowSpan={1} />;
									})}
								</TableRow>
							)}
							<TableRow>
								{visibleColumns.map((key) => {
									const colDef = columns[key];
									return (
										<TableCell
											key={key}
											align={isFieldDisabled(key) && ['integer', 'decimal', 'date'].includes(get(colDef, 'type')) ? 'right' : 'left'}
											sx={{
												padding: '0.5rem',
												width: get(props, `fieldProps.${key}.width`, 'auto'),
												maxWidth: get(props, `fieldProps.${key}.maxWidth`, '0'),
												writingMode: get(props, `fieldProps.${key}.wirtingMode`, 'initial'),
											}}
										>
											<>
												{getColumnLabel(get(colDef, 'path'), key, get(props, 'fieldProps'), apiName)}
												{getToolTip(get(props, 'fieldProps', {}), key) && (
													<MUIToolTip title={getToolTip(get(props, 'fieldProps', {}), key)}>
														<Help fontSize='small' color='primary' sx={{ ml: '5px' }} />
													</MUIToolTip>
												)}
											</>
										</TableCell>
									);
								})}
								{!isDisabled && !get(props, 'hideDeleteButton') && (
									<TableCell className='pinned-right-column' sx={{ padding: '0.5rem 1rem', backgroundColor: 'transparent' }} />
								)}
							</TableRow>
						</TableHead>
						<TableBody>
							{(fieldValue || []).map((eachRow: any, rowIndex: number) => {
								const isMilestone = get(eachRow, rowTypeField) === milestoneValue;
								const rowBackground = getRowBackground(eachRow);
								return (
									<TableRow key={rowIndex} sx={{ backgroundColor: rowBackground }}>
										{visibleColumns.map((columnKey) => {
											const colDef = columns[columnKey];
											const attributePathWithIndex = mapTablePath(colDef.path, rowIndex, tablePath);
											const shouldHideForMilestone = isMilestone && hideColumnsForMilestone.includes(columnKey);
											return (
												<TableCell
													key={columnKey}
													sx={{ padding: '0.5rem', maxWidth: 0, minWidth: get(props, `fieldProps.${columnKey}.width`, 'auto') }}
													align={
														isFieldDisabled(columnKey) && ['integer', 'decimal', 'date'].includes(get(colDef, 'type')) ?
															'right'
														:	'left'
													}
												>
													{shouldHideForMilestone ?
														<Typography>-</Typography>
													: isFieldDisabled(columnKey) ?
														<Typography>
															{getDisplayText(
																colDef.type,
																getDisplayValue(columnKey, eachRow),
																get(props, `fieldProps.${columnKey}.format`, ''),
																get(props, `fieldProps.${columnKey}.customLabel`, ''),
																get(props, `fieldProps.${columnKey}.pathForLabel`, ''),
															)}
														</Typography>
													:	<FormFields
															{...colDef}
															label=''
															type={get(props, `fieldProps.${columnKey}.type`, colDef.type)}
															formValues={eachRow}
															formErrors={get(props, 'formErrors')}
															format={get(props, `fieldProps.${columnKey}.format`)}
															path={columnKey}
															onChange={(path, value) => {
																handleChange(path, value, rowIndex);
															}}
															disabled={isDisabled || get(props, 'disabledColumns', []).includes(columnKey)}
															editableTablePathWIthIndex={attributePathWithIndex}
															allowOnlyImage={get(props, `fieldProps.${columnKey}.allowOnlyImage`)}
															size='small'
															options={get(props, `fieldProps.${columnKey}.options`, colDef.options)}
															multiple={get(props, `fieldProps.${columnKey}.multiple`)}
															stringMode={get(props, `fieldProps.${columnKey}.stringMode`)}
															separator={get(props, `fieldProps.${columnKey}.separator`)}
															maxCharLength={get(props, `fieldProps.${columnKey}.maxCharLength`)}
														/>
													}
												</TableCell>
											);
										})}
										{!get(props, 'hideDeleteButton') && !isDisabled && (
											<TableCell className='pinned-right-column' sx={{ padding: '0.5rem 1rem', backgroundColor: rowBackground }}>
												<IconButton onClick={() => handleDelete(rowIndex)} disabled={isDisabled}>
													<DeleteIcon />
												</IconButton>
											</TableCell>
										)}
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>

				{!isDisabled && (
					<Stack direction='row' spacing={1.5} sx={{ mt: 1.5 }}>
						<Button variant='outlined' size='medium' startIcon={<AddCircleOutlineIcon />} onClick={handleAddAufgabe}>
							{addAufgabeLabel}
						</Button>
						<Button variant='contained' size='medium' startIcon={<AddCircleOutlineIcon />} onClick={handleAddMilestone}>
							{addMilestoneLabel}
						</Button>
					</Stack>
				)}
			</Box>
		</>
	);
};

export default SimpleZeitplanungTable;

function getColumnLabel(path: string, key: string, fieldProps: any, apiName: string): string {
	if (fieldProps && fieldProps[key] && fieldProps[key].label) {
		return fieldProps[key].label;
	}

	const parts: string[] = path.split('.');
	const lastPart: string = parts.pop()!;

	if (lastPart.startsWith('Zelle')) {
		const zelleNumber: string = lastPart.match(/\d+/)![0];
		let blockPart: string = parts.pop()!;
		blockPart = blockPart.includes('Block_') ? blockPart : blockPart;
		const modifiedBlockPart: string = blockPart + `_Spalte_${zelleNumber}`;
		return fieldNameTranslation(modifiedBlockPart, apiName);
	}
	return fieldNameTranslation(lastPart, apiName);
}

function getToolTip(fieldProps: any, key: string) {
	return get(fieldProps, `${key}.tooltip`, '');
}

function getDisplayText(type: string, value: any, format?: string, label?: string, keyForlabel?: string) {
	if (value === undefined || value === null) return '';
	if (label) return label;

	switch (type) {
		case 'decimal':
			if (format === 'currency') {
				return DisplayEuro(value);
			}
			if (format === 'percentage') {
				return DisplayDecimal(value, true);
			}
			return DisplayDecimal(value);
		case 'date':
			return DisplayDate(value);
		case 'enumeration':
			return enumT(value);
		case 'boolean':
			return isTrue(value) ? 'Ja' : 'Nein';
		case 'relation':
			return get(value, keyForlabel, '');
		case FORM_FIELDS_TYPES.media:
			return <SingleUpload label={''} fieldValue={value} updateFieldValue={(_e) => {}} isDisabled={true} error={false} errorMessage={''} />;
		default:
			return value;
	}
}
